import { useState, useEffect } from "react";
import { useToaster, Button, SelectPicker } from "rsuite";
import TableComponent, { ColumnConfig } from "../../components/TableComponent";
import {
  useGetSenarioDataMutation,
  useGetConsensusFilterMutation,
  useGetDropDownDependentValuesMutation,
  useUpdateScenarioDataMutation,
  useGetUpdateSoqMutation,
  useExportScenarioDataMutation,
} from "../../services/redux/api/consensus";
import FilterComponent from "../../components/FilterComponent";
import upArrow from "../../assets/uparrowFilter.svg";
import downArrow from "../../assets/downarrowFilter.svg";
import CustomLoader from "../../common/CustomLoader/CustomLoader";
import moment from "moment";
import reload from "../../assets/reload.svg";

// In types.ts
export interface IUpdateScenarioData {
  AdjustedForecast: number;
  AdjustedSOQ: number;
  BranchId: string;
  ErpItemId: string;
  Initial_AdjustedSOQ: number;
  PageNumber: string;
  PriceMultiplier: number;
  ScenarioPrice: number;
  ScenarioSelection: number;
  UserForecast: number;
  UserSOQSelection: number;
  UserSelection: number;
  aFlag: number;   // Ensure this is a number
  forecast_date: string;
  isPoundSelected: boolean;
  pFlag: number;   // Ensure this is a number
  replicate_date: string;
}

export interface IUpdateScenarioPayload {
  data: IUpdateScenarioData[];
}



const firstDropdownOptions = [
  { label: "-30%", value: -30 },
  { label: "-25%", value: -25 },
  { label: "-20%", value: -20 },
  { label: "-15%", value: -15 },
  { label: "-10%", value: -10 },
  { label: "-5%", value: -5 },
  { label: "0%", value: 0 },
  { label: "5%", value: 5 },
  { label: "10%", value: 10 },
  { label: "15%", value: 15 },
  { label: "20%", value: 20 },
  { label: "25%", value: 25 },
  { label: "30%", value: 30 },
];

const secondDropdownOptions = [
  { label: "Non Printed Ad", value: "Non Printed Ad" },
  { label: "Front", value: "Front of the page" },
  { label: "Middle", value: "Middle of the page" },
  { label: "No Ad", value: "No Ad" },
  { label: "Back", value: "Back of the page" },
];

// TypeScript interface for table data
interface ScenarioDataItem {
  VendorName: string;
  ErpItemId: string;
  itemDescription: string;
  BranchName: string;
  AvgTotalPoundsAd_6Weeks: number;
  AvgTotalPoundsFlow_6Weeks: number;
  ForecastWeek_3: number;
  ScenarioSelection: number;
  ExpectedPrice: number;
  ExpectedPageNumber: string;
  ScenarioPrice: number;
  AdjustedForecast: number;
  Initial_AdjustedForecast: number;
  Initial_SOQ_week2: number;
  BranchId: string;
  Overrideby: string;
  UserForecast: number;
  AdjustedSOQ: number;
  overrideQtyInAlerts: number;
  UserSOQSelection: number;
  aFlag: number;
  pFlag: number;
  PageNumber: string;
  PriceMultiplier: number;
  UserSelection: number;
  NetUnitPounds: number;
  Initial_AdjustedSOQ: number;
  SOQ_2Pounds: number,
  OpenPoPounds: number,
  OnhandPounds: number,
  TargetStockPounds: number,
  dropdownBasedForecast: { ScenarioPrice: number | null, UserForecast: number | null }
}

const Consensus = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [modifiedRows, setModifiedRows] = useState<any[]>([]);
  const toaster = useToaster();
  const [getScenarioData, { isLoading, error }] = useGetSenarioDataMutation();
  const [getDropDownDependentValues] = useGetDropDownDependentValuesMutation();
  const [updateScenarioData] = useUpdateScenarioDataMutation();
  const [
    getConsensusFilter,
    { isLoading: isFilterLoading, data: filterData, error: filterError },
  ] = useGetConsensusFilterMutation();

  // Hook usages
  const [
    getUpdateSoq,
    { isLoading: isSOQLoading, data: soqData, error: soqError },
  ] = useGetUpdateSoqMutation();

  const [
    exportScenario,
    { isLoading: isExportLoading, data: exportData, error: exportError },
  ] = useExportScenarioDataMutation();
  const [exportDataSet, setExportDataSet] = useState<any[]>([]); // Export data
  const [exportColumns, setExportColumns] = useState<any[]>([]); // Export headers

  const filterShowHandler = () => {
    setIsVisible((prev) => !prev);
  };

  // State for columns and tableData
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    dc: [],
    vendorName: [],
    category: [],
    subCategory: [],
    itemDesc: [],
    itemId: [],
    alerts: [],
  });

  const [tempFilters, setTempFilters] = useState({
    dc: [],
    vendorName: [],
    category: [],
    subCategory: [],
    itemDesc: [],
    itemId: [],
    alerts: [],
  });
  const [totalSummaryInput, setTotalSummaryInput] = useState({
    totalForecastWeek_3: 0,
    totalSOQ_2Pounds: 0,
  });

  const [initialTotalSummaryInput, setInitialState] = useState({
    totalForecastWeek_3: 0,
    totalSOQ_2Pounds: 0,
    lastUpdated: "",
    forecast_date: "",
    replicate_date: "",
  });

  const [selectedFilterData, setSelectedFilterData] = useState<{
    [key: string]: string[];
  }>({});
  const [tableHeight, setTableHeight] = useState(0);
  const [originalTableState, setOriginalTableState] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1); // Active page for pagination
  const staticFilters = {
    adjustmentWeek: [{ filterValue: "3", displayValue: "3" }],
    viewBy: [
      { filterValue: "cases", displayValue: "Cases" },
      { filterValue: "po", displayValue: "Pounds" }, // Display as "Pounds"
    ],
  };

  const calculateTableHeight = () => {
    const totalHeight = window.innerHeight;
    const headerHeight = 80;
    const filterBarHeight = isVisible ? 170 : 120;
    const paginationHeight = 50;
    const minTableHeight = 300; // Add minimum height

    // Calculate available height for the table
    let availableHeight = totalHeight - (headerHeight + filterBarHeight + paginationHeight);

    // Ensure table doesn't get too small
    availableHeight = Math.max(availableHeight, minTableHeight);

    setTableHeight(availableHeight);
  };


  useEffect(() => {
    calculateTableHeight(); // Initial height calculation
    window.addEventListener("resize", calculateTableHeight); // Adjust on resize

    return () => {
      window.removeEventListener("resize", calculateTableHeight); // Cleanup listener
    };
  }, [isVisible]);

  const calculateAdjustedForecast = (item: ScenarioDataItem) => {
    const viewBy = selectedFilterData?.viewBy ?? "cases";
    const isPoundsFlag = viewBy?.includes("po");
    const adjForecast = item.UserSelection ? Number(item.Initial_AdjustedForecast) : Number(item.AdjustedForecast)
    // Updated
    if (isPoundsFlag) {
      // const result = Math.round(Number(item.AdjustedForecast) * 10) / 10;
      const result = Math.floor(adjForecast * item.NetUnitPounds);
      return result;
    } else {
      const result = Math.floor(adjForecast);
      return result;
    }

  };

  const calculateFinalSOQ = (item: ScenarioDataItem) => {
    const viewBy = selectedFilterData?.viewBy ?? "cases";
    const isPoundsFlag = viewBy?.includes("po");

    // if (isNaN(Number(item.SOQ_2Pounds))) {
    //   return 0;
    // }

    if (isPoundsFlag) {
      const result = Math.round(Number(item.SOQ_2Pounds) * Number(item.NetUnitPounds));
      return result;
    } else {
      const result = Math.round(Number(item.SOQ_2Pounds));
      return result;
    }
  }

  const calculateScenarioUnitPrice = (item: ScenarioDataItem) => {

    // Handle null/undefined/empty cases
    if (!item.ScenarioPrice && item.ScenarioPrice !== 0) {
      return "-";
    }

    // If ScenarioPrice is not a number, return "-"
    const price = Number(item.ScenarioPrice);
    if (isNaN(price)) {
      return "-";
    }

    // Format valid price
    const result = `$${price.toFixed(2)}`;
    return result;
  }

  const calculateOnhandPounds = (item: ScenarioDataItem) => {
  const viewBy = selectedFilterData?.viewBy ?? "cases";
  const isPoundsFlag = viewBy?.includes("po");

  if (isPoundsFlag) {
    // For "po" view: Return Target Stock as is
    const result = Math.round(Number(item.OnhandPounds) * Number(item.NetUnitPounds));
    return result.toFixed(0);
  } else {
    // For "cases" view: Target Stock / NetUnitPounds
    if (!item.NetUnitPounds || Number(item.NetUnitPounds) === 0) {
      return 0;
    }
    const result = Math.round(Number(item.OnhandPounds));
    return result.toFixed(0);
  }
};
const calculateTargetStock = (item: ScenarioDataItem) => {
  const viewBy = selectedFilterData?.viewBy ?? "cases";
  const isPoundsFlag = viewBy?.includes("po");

  if (isPoundsFlag) {
    // For "po" view: Return Target Stock as is
    const result = Math.round(Number(item.TargetStockPounds) * Number(item.NetUnitPounds));
    return result.toFixed(0);
  } else {
    // For "cases" view: Target Stock / NetUnitPounds
    if (!item.NetUnitPounds || Number(item.NetUnitPounds) === 0) {
      return 0;
    }
    const result = Math.round(Number(item.TargetStockPounds));
    return result.toFixed(0);
  }
};

const calculateOpenPoPounds = (item: ScenarioDataItem) => {
  const viewBy = selectedFilterData?.viewBy ?? "cases";
  const isPoundsFlag = viewBy?.includes("po");

  if (isPoundsFlag) {
    // For "po" view: Return Open PO as is
    const result = Math.round(Number(item.OpenPoPounds) * 10) / 10;
    return result.toFixed(0);
  } else {
    // For "cases" view: Open PO / NetUnitPounds
    if (!item.NetUnitPounds || Number(item.NetUnitPounds) === 0) {
      return 0;
    }
    const result = Math.round(Number(item.OpenPoPounds) / Number(item.NetUnitPounds));
    return result.toFixed(0);
  }
};


const calculateAdjustSOQForOpenPO = (item: ScenarioDataItem) => {
  const viewBy = selectedFilterData?.viewBy ?? "cases";
  const isPoundsFlag = viewBy?.includes("po");
  
  // Get the final SOQ value
  let finalSOQ = 0;
  if (isPoundsFlag) {
    finalSOQ = Math.round(Number(item.SOQ_2Pounds) * 10) / 10;
  } else {
    finalSOQ = Math.round(Number(item.SOQ_2Pounds));
  }
  
  // Get the Open PO value (reuse the calculateOpenPoPounds function)
  const openPO: any  = calculateOpenPoPounds(item);
  
  // Calculate Adjusted SOQ = final SOQ - Open PO
  // If the result is negative, return 0
  const difference = finalSOQ - openPO;
  
  if (difference <= 0) {
    return 0;
  }
  
  // Round the result appropriately based on the view
  if (isPoundsFlag) {
    return Math.round((difference * 10) / 10).toFixed(0); // Round to 1 decimal place for pounds
  } else {
    return Math.round(difference).toFixed(0); // Round to whole number for cases
  }
};

  const fetchData = async (currentPage: number, filterData: any) => {

    const activeFilters = Object.entries(filterData).reduce(
      (acc: any, [key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          // Map the old keys to new keys
          switch (key) {
            case 'dc':
              acc['BranchId'] = values;
              break;
            case 'vendorName':
              acc['vName'] = values;
              break;
            case 'itemDesc':
              acc['itemDescription'] = values;
              break;
            case 'itemId':
              acc['ErpItemId'] = values;
              break;
            case 'subCategory':
              acc['subcategory'] = values;
              break;
            case 'alert':
              acc['alerts'] = values;
              break;
            default:
              acc[key] = values;
          }
        }
        return acc;
      },
      { UCode: [] }  // Initialize with empty UCode array
    );

    // Ensure default value for `viewBy` if not set
    if (!activeFilters.viewBy || activeFilters.viewBy.length === 0) {
      activeFilters.viewBy = ["cases"];
    }

    const payload = {
      WeekNumber: 3,
      filter: activeFilters,
      order: {
        columnName: "ForecastWeek_3",
        isAsc: false,
      },
      page: {
        number: currentPage - 1,
        size: 20
      },
    };

    try {
      const result = await getScenarioData(payload).unwrap();

      if (result?.data) {
        const mappedData = result.data.map(
          (item: ScenarioDataItem, index: number) => ({
            id: index,
            BranchId: item?.BranchId,
            VendorName: item.VendorName,
            Item: `${item.ErpItemId} - ${item.itemDescription}`,
            DC: item?.BranchId,
            AvgWeeklyCasesAd: Math.floor(item.AvgTotalPoundsAd_6Weeks) || 0,
            AvgWeeklyCasesFlow: Math.floor(item.AvgTotalPoundsFlow_6Weeks) || 0,

            TotalForecast: Math.floor(item.ForecastWeek_3),

            ForecastNewStores: item.ScenarioSelection || 0,
            ExpectedUnitPrice: `$${item.ExpectedPrice?.toFixed(2)}`,
            ExpectedAdType: item.ExpectedPageNumber || "No Ad",
            ScenarioUnitPrice: item.ScenarioSelection ? (item.PriceMultiplier === 0 ? 0 : (item.PriceMultiplier ?? "Select")) : "Select",
            AdTypeScenario: item.ScenarioSelection ? item.PageNumber : "Select", //"Select",
            FinalUnitPrice: calculateScenarioUnitPrice(item),/* item.ScenarioPrice */        // `$${item.ScenarioPrice?.toFixed(2)} || "-"`,
            PlannerOverrideInAlerts: Math.floor(item.overrideQtyInAlerts),
            OverrideBy: item?.Overrideby || "-",
            FinalForecastOverride: item.UserForecast ? Math.floor(item.UserForecast) : null,
            FinalAdjustedForecast: calculateAdjustedForecast(item),      // Math.floor(item.AdjustedForecast) || 0,
            SOQOverride: item.UserSOQSelection > 0 ? Math.floor(item.Initial_AdjustedSOQ) : null,
            FinalSOQ: calculateFinalSOQ(item),// Math.floor(item.Initial_SOQ_week2) || 0,
            UserSOQSelection: item.UserSOQSelection,
            aFlag: item.aFlag,
            pFlag: item.pFlag,

            AdjustedForecast: item.AdjustedForecast,

            ErpItemId: item.ErpItemId,
            PageNumber: item.PageNumber,
            PriceMultiplier: item.PriceMultiplier,
            UserSelection: item.UserSelection,
            ScenarioPrice: item.ScenarioPrice,
            NetUnitPounds: item.NetUnitPounds,
            TargetStockPounds: calculateTargetStock(item),
            OnhandPounds: calculateOnhandPounds(item),
            OpenPoPounds: calculateOpenPoPounds(item),
            AdjustSOQForOpenPO: calculateAdjustSOQForOpenPO(item),
            dropdownBasedForecast: { ScenarioPrice: null, UserForecast: null }
          })
        );
        setTableData(mappedData);

        if (result?.summary[0].totalCount) {
          setTotalRows(result?.summary[0].totalCount);
        }
        if (result?.summary && result.summary.length > 0) {
          const totalSummary = result.summary[0];

          const { totalForecastWeek_3, totalSOQ_2Pounds } = result.summary[0];
          setTotalSummaryInput({
            totalForecastWeek_3: Math.floor(totalForecastWeek_3),
            totalSOQ_2Pounds: Math.floor(totalSOQ_2Pounds),
          });
          setInitialState({
            totalForecastWeek_3: Math.floor(totalForecastWeek_3),
            totalSOQ_2Pounds: Math.floor(totalSOQ_2Pounds),
            lastUpdated: result?.lastUpdateDate,
            replicate_date: result.summary[0].replicate_date,
            forecast_date: result.summary[0].forecast_date
          });

          setColumns([
            {
              key: "VendorName",
              title: "Vendor Name",
              width: 150,
              sortable: true,
              fixed: "left",
            },
            {
              key: "Item",
              title: "Item",
              width: 200,
              sortable: true,
              fixed: "left",
            },
            {
              key: "DC",
              title: "DC",
              width: 150,
              sortable: true,
              fixed: "left",
            },
            {
              key: "AvgWeeklyCasesAd",
              title: `Avg. Weekly<br/>Cases (Ad)<br/><span style='color:#2EABDF;'>${Math.floor(
                totalSummary.totalAvgTotalPoundsAd_6Weeks
              )}</span>`,
              width: 150,
              sortable: true,
            },
            {
              key: "AvgWeeklyCasesFlow",
              title: `Avg. Weekly<br/>Cases (No Ad)<br/><span style='color:#2EABDF;'>${Math.floor(
                totalSummary.totalAvgTotalPoundsFlow_6Weeks
              )}</span>`,
              width: 150,
              sortable: true,
            },
            {
              key: "TotalForecast",
              title: `Total<br/>Forecast<br/><span style='color:#2EABDF;'>${Math.floor(
                totalSummary.totalForecastWeek_3
              )}</span>`,
              width: 150,
              sortable: true,
            },
            {
              key: "ForecastNewStores",
              title: "Forecast New Stores",
              width: 150,
              sortable: true,
            },
            {
              key: "ExpectedUnitPrice",
              title: "Expected Unit Price",
              width: 150,
              sortable: true,
            },
            {
              key: "ExpectedAdType",
              title: "Expected Ad Type",
              width: 150,
              sortable: true,
            },
            {
              key: "ScenarioUnitPrice",
              title: "Scenario Unit Price",
              width: 150,
              cellType: "dropdown",
              options: firstDropdownOptions,
            },
            {
              key: "AdTypeScenario",
              title: "Ad Type Scenario",
              width: 150,
              cellType: "dropdown",
              options: secondDropdownOptions,
            },
            {
              key: "FinalUnitPrice",
              title: "Final Unit Price",
              width: 150,
              sortable: true,
            },
            {
              key: "PlannerOverrideInAlerts",
              title: "Planner Override <br/> in Alerts",
              width: 150,
              sortable: true,
            },
            {
              key: "OverrideBy",
              title: "Override By",
              width: 150,
              sortable: true,
            },
            {
              key: "FinalForecastOverride",
              title: "Final Adjusted Override",
              width: 150,
              cellType: "input",
            },
            {
              key: "FinalAdjustedForecast",
              title: `Final Adjusted<br/>Forecast<br/><span style='color:#2EABDF;'>${Math.floor(
                totalForecastWeek_3
              )}</span>`,
              width: 150,
              sortable: true,
            },
            // {
            //   key: "TargetStockPounds",
            //   title: "Target Stock",
            //   width: 150,
            //   sortable: true,
            // },
            {
              key: "OnhandPounds",
              title: "OnHand",
              width: 150,
              sortable: true,
            },
            {
              key: "SOQOverride",
              title: "SOQ Override",
              width: 150,
              cellType: "input",
            },
            {
              key: "FinalSOQ",
              title: `Final SOQ<br/><span style='color:#2EABDF;'>${Math.floor(
                totalSOQ_2Pounds
              )}</span>`,
              width: 150,
              sortable: true,
            },
            {
              key: "OpenPoPounds",
              title: "Open PO",
              width: 150,
              sortable: true,
            },
            {
              key: "AdjustSOQForOpenPO",
              title: "Adjusted SOQ",
              width: 150,
              sortable: true,
            },
          ]);
        }
      }else{
        setTableData([]);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchFilterData = async (filterData: any) => {

    const formattedData = {
      dc: filterData.dc || [],
      vendorName: filterData.vendorName || [],
      category: filterData.category || [],
      subCategory: filterData.subCategory || [],
      itemDesc: filterData.itemDesc || [],
      itemId: filterData.itemId || [],
      alerts: filterData.alerts || []
    };
    console.log("formattedData", formattedData);

    try {
      const response = await getConsensusFilter(formattedData).unwrap(); // Fetch filters from API

      if (response?.data) {
        setFilters(response.data);
      } else {
        console.warn("No filters found in the response.");
      }
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  // Handle individual filter changes
  const handleFilterChange = async (filterKey: string, values: string[]) => {
    console.log("filterKey", filterKey);
    console.log("values", values);

    const newState = await new Promise(resolve => {
      setSelectedFilterData(prev => {
        const updatedState = {
          ...prev,
          [filterKey]: values
        };
        resolve(updatedState);
        return updatedState;
      });

      setTempFilters(prev => ({
        ...prev,
        [filterKey]: values
      }));
    });

    await fetchFilterData(newState)
  };

  const handleApplyFilters = async () => {
    // Update the main filter state
    setSelectedFilterData(tempFilters);

    // Format filters for API
    const activeFilters = Object.entries(tempFilters).reduce(
      (acc: any, [key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          switch (key) {
            case 'dc':
              acc['BranchId'] = values;
              break;
            case 'vendorName':
              acc['vName'] = values;
              break;
            case 'itemDesc':
              acc['itemDescription'] = values;
              break;
            case 'itemId':
              acc['ErpItemId'] = values;
              break;
            case 'subCategory':
              acc['subcategory'] = values;
              break;
            default:
              acc[key] = values;
          }
        }
        return acc;
      },
      { UCode: [] }
    );

    // Ensure default viewBy
    if (!activeFilters.viewBy || activeFilters.viewBy.length === 0) {
      activeFilters.viewBy = ["cases"];
    }

    try {
      // Fetch filters
      //  await fetchData(activeFilters);
      // Fetch data with new filters
      await fetchData(currentPage, activeFilters);
    } catch (error) {
      console.error("Error applying filters:", error);
    }
  };

  // Function to handle Clear button click
  const handleClearFilters = () => {
    const emptyFilters = {
      dc: [],
      vendorName: [],
      category: [],
      subCategory: [],
      itemDesc: [],
      itemId: [],
      alerts: [],
    };

    // Clear both temporary and main filters
    setTempFilters(emptyFilters);
    setSelectedFilterData(emptyFilters);

    // Fetch data with cleared filters
    fetchData(currentPage, emptyFilters);
  };


  const handleRowSelect = (selectedKeys: any[], rowData: any) => {
    if (!rowData) {
      console.warn("No row data found.");
      return;
    }

    // Normalize keys (e.g., `DC` -> `dc`)
    setSelectedFilterData((prevFilters) => ({
      ...prevFilters,
      dc: rowData["DC"] ? [rowData["DC"]] : [],
      vendorName: rowData["VendorName"] ? [rowData["VendorName"]] : [],
      category: rowData["Category"] ? [rowData["Category"]] : [],
      subCategory: rowData["SubCategory"] ? [rowData["SubCategory"]] : [],
      itemId: rowData["Item"] ? [rowData["Item"].split(" - ")[0]] : [],
      alert:
        rowData["PlannerOverrideInAlerts"] &&
          rowData["PlannerOverrideInAlerts"] !== "-"
          ? [rowData["PlannerOverrideInAlerts"]]
          : [],
    }));
  };

  // Handle dropdown open events if needed (e.g., additional data fetch)
  const filterHandler = () => {
    // Additional logic can go here if needed
  };

  const handleDataChange = async (updatedData: any[], modifiedRows: any[]) => {
    // Update `tableData` with the new data
    console.log("modifiedRows", modifiedRows);
    
    setTableData(updatedData);
    setModifiedRows([modifiedRows]);
    let updatedForecastTotal = initialTotalSummaryInput.totalForecastWeek_3;
    let updatedSOQTotal = initialTotalSummaryInput.totalSOQ_2Pounds;

    // Recalculate totals using the entire `updatedData` array
    modifiedRows.forEach((row) => {
      let forecastValue = "";
      if (row.FinalForecastOverride) {
        if (row.FinalForecastOverride >= 0) {
          forecastValue = row.FinalForecastOverride;
        }
      } else if (row.dropdownBasedForecast.UserForecast) {
        forecastValue = row.dropdownBasedForecast.UserForecast;
      } else {
        forecastValue = row.FinalAdjustedForecast
      }
      // const forecastValue = parseFloat(row.FinalForecastOverride || "0");
      const soqValue = Number(row.SOQOverride);

      if (forecastValue != ""/*  && forecastValue!= undefined && forecastValue != null */) {
        updatedForecastTotal += parseInt(forecastValue);
      }
      updatedSOQTotal += soqValue;
    });
  

    // Update `totalSummaryInput` with the recalculated totals
    setTotalSummaryInput({
      totalForecastWeek_3: updatedForecastTotal,
      totalSOQ_2Pounds: updatedSOQTotal,
    });

    // Update column headers with recalculated totals
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "FinalAdjustedForecast") {
          return {
            ...col,
            title: `Final Adjusted<br/>Forecast<br/><span style='color:#2EABDF;'>${Math.floor(
              updatedForecastTotal
            )}</span>`,
          };
        }

        if (col.key === "FinalSOQ") {
          return {
            ...col,
            title: `Final<br/>SOQ<br/><span style='color:#2EABDF;'>${Math.floor(
              updatedSOQTotal
            )}</span>`,
          };
        }

        return col;
      })
    );
  };

  useEffect(() => {
    if (tableData.length > 0 && originalTableState.length === 0) {
      setOriginalTableState(JSON.parse(JSON.stringify(tableData)));
    }
  }, [tableData]);

  const handleRowDropdownAction = async (rowId: number, dropdownValues: any) => {

    const originalRowData: any = originalTableState.find((row: any) => row.id === rowId);
    if (!originalRowData) {
      console.error(`Row with ID ${rowId} not found in original state.`);
      return;
    }

    // If dropdown values are cleared or invalid, restore the original values
    if (
      !dropdownValues.Item ||
      dropdownValues.Item === "Select" ||
      !dropdownValues.ScenarioUnitPrice ||
      dropdownValues.ScenarioUnitPrice === "Select"
    ) {
      console.log("Dropdown values removed. Reverting to previous values...");

      let finalAdjForecast = originalRowData.FinalAdjustedForecast || "";
      /* Checking if on emptying dropdown, we still have user entered override. If yes, setting final adj forecast to that value or else initial value */
      if (tableData[rowId].FinalForecastOverride) {
        if (tableData[rowId].FinalForecastOverride >= 0) {
          finalAdjForecast = tableData[rowId].FinalForecastOverride
        } else {
          finalAdjForecast = tableData[rowId].TotalForecast
        }
      } else {
        finalAdjForecast = tableData[rowId].TotalForecast
      }
      setModifiedRows((prevModifiedRows) => {
        // Create a unique key for the current row
        const currentRowKey = `${dropdownValues.ErpItemId}-${dropdownValues.BranchId}`;

        // Filter out any existing entries with the same ErpItemId and BranchId
        const filteredRows = prevModifiedRows.filter(row =>
          `${row.ErpItemId}-${row.BranchId}` !== currentRowKey
        );

        const updatedRow = {
          ...dropdownValues,
          id: rowId,
          FinalUnitPrice: originalRowData.FinalUnitPrice || "",
          FinalAdjustedForecast: finalAdjForecast,
          // PageNumber: dropdownValues.AdTypeScenario,
          dropdownBasedForecast: { ScenarioPrice: null, UserForecast: null },
        };

        return [...filteredRows, updatedRow];
      });


      setModifiedRows((prevModifiedRows) => {
        const uniqueRowsMap = new Map();

        prevModifiedRows.forEach((row) => {
          const key = `${row.ErpItemId}-${row.BranchId}`;
          uniqueRowsMap.set(key, row);
        });

        // Create a unique key for the current row
        const currentRowKey = `${dropdownValues.ErpItemId}-${dropdownValues.BranchId}`;

        // Add or update the current row in the map
        uniqueRowsMap.set(currentRowKey, {
          ...dropdownValues,
          id: rowId,
          FinalUnitPrice: originalRowData.FinalUnitPrice || "",
          FinalAdjustedForecast: finalAdjForecast,
          // PageNumber: dropdownValues.AdTypeScenario,
        });

        // Convert the map back to an array
        return Array.from(uniqueRowsMap.values());
      });

      // Restore tableData
      setTableData((prevData) =>
        prevData.map((row) =>
          row.id === rowId
            ? {
              ...row,
              FinalUnitPrice: originalRowData.FinalUnitPrice || "", // Revert FinalUnitPrice
              FinalAdjustedForecast: finalAdjForecast, // Revert FinalAdjustedForecast
              dropdownBasedForecast: { ScenarioPrice: null, UserForecast: null }
            }
            : row
        )
      );

      return;
    }

    const activeFilters = Object.entries(selectedFilterData).reduce(
      (acc: any, [key, values]) => {
        if (values.length > 0) {
          acc[key] = values;
        }
        return acc;
      },
      {}
    );

    if (!activeFilters.viewBy || activeFilters.viewBy.length === 0) {
      activeFilters.viewBy = ["cases"];
    }

    const payload = {
      WeekNumber: 3,
      ErpItemId: dropdownValues.Item.split(" - ")[0],
      branchId: dropdownValues.BranchId,
      PageNumber: dropdownValues.AdTypeScenario,
      PriceMultiplier:
        parseFloat(dropdownValues?.ScenarioUnitPrice) / 100,
      viewBy: activeFilters.viewBy[0],
    };

    try {
      const response = await getDropDownDependentValues(payload).unwrap();

      const ScenarioPrice = response?.data?.[0]?.ScenarioPrice;
      const ScenarioForecast = Math.floor(response?.data?.[0]?.ScenarioForecast);

      let forecastCollection = {
        userEnteredOverride: tableData[rowId].FinalForecastOverride,
        initialDefaultForecast: tableData[rowId].UserForecast,
        initialAdjustedForecastCalculated: tableData[rowId].AdjustedForecast,
      }
      let finalObj = {
        scenarioPrice: ScenarioPrice,
        FinalAdjustedForecast: 0,
        dropdownBasedForecastScenarioPrice: ScenarioPrice,
        dropdownBasedForecastUserForecast: ScenarioForecast,
      }

      if (forecastCollection.userEnteredOverride) {
        if (forecastCollection.userEnteredOverride >= 0) {
          finalObj.FinalAdjustedForecast = forecastCollection.userEnteredOverride
        }
      } else if (ScenarioForecast) {
        finalObj.FinalAdjustedForecast = ScenarioForecast;
      } else {
        finalObj.FinalAdjustedForecast = originalRowData.FinalAdjustedForecast
      }


      setModifiedRows((prevModifiedRows) => {
        const uniqueRowsMap = new Map();

        prevModifiedRows.forEach((row) => {
          const key = `${row.ErpItemId}-${row.BranchId}`;
          uniqueRowsMap.set(key, row);
        });

        // Create a unique key for the current row
        const currentRowKey = `${dropdownValues.ErpItemId}-${dropdownValues.BranchId}`;

        // Add or update the current row in the map
        uniqueRowsMap.set(currentRowKey, {
          ...dropdownValues,
          id: rowId,
          FinalUnitPrice: ScenarioPrice ? `$${ScenarioPrice.toFixed(2)}` : originalRowData.FinalUnitPrice || "",
          FinalAdjustedForecast: finalObj.FinalAdjustedForecast,
          PageNumber: dropdownValues.AdTypeScenario,
          dropdownBasedForecast: { ScenarioPrice: ScenarioPrice, UserForecast: ScenarioForecast },
        });

        // Convert the map back to an array
        return Array.from(uniqueRowsMap.values());
      });

      setTableData((prevModifiedRows) => {

        const updatedRows = prevModifiedRows.map((row) =>
          row.id === rowId
            ? {
              ...row,
              FinalUnitPrice: ScenarioPrice ? `$${ScenarioPrice.toFixed(2)}` : originalRowData.FinalUnitPrice || "",
              FinalAdjustedForecast: finalObj.FinalAdjustedForecast,
              PageNumber: dropdownValues.AdTypeScenario,
              dropdownBasedForecast: { ScenarioPrice: ScenarioPrice, UserForecast: ScenarioForecast },
            }
            : row
        );

        // If rowId does not exist in modifiedRows, push the new row
        const isRowExisting = updatedRows.some((row) => row.id === rowId);
        if (!isRowExisting) {
          const newRow = {
            ...dropdownValues,
            id: rowId,
            FinalUnitPrice: ScenarioPrice ? `$${ScenarioPrice.toFixed(2)}` : originalRowData.FinalUnitPrice || "",
            FinalAdjustedForecast: finalObj.FinalAdjustedForecast,
            PageNumber: dropdownValues.AdTypeScenario,
            dropdownBasedForecast: { ScenarioPrice: ScenarioPrice, UserForecast: ScenarioForecast },
          };
          updatedRows.push(newRow);
        }


        return updatedRows;
      });



    } catch (err) {
      console.error("Error fetching dropdown dependent values:", err);
    }
  };

  const handleSave = async () => {
    // Ensure there are rows to modify
    if (modifiedRows.length === 0) {
      return;
    }

    const uniqueModifiedRows = Array.from(
      new Map(
        modifiedRows.map((row) => [`${row.ErpItemId}-${row.BranchId}`, row])
      ).values()
    );

    // Get today's ISO date
    const allRows = uniqueModifiedRows.flat();
console.log("allRows", allRows);

    // Transform `allRows` into the required API payload
    const payload: IUpdateScenarioPayload = {
      data: allRows.map((row): IUpdateScenarioData => {
        
        const hasFinalForecast =
          row.FinalForecastOverride !== undefined &&
          row.FinalForecastOverride !== null &&
          row.FinalForecastOverride !== "";

      let userSoqSelection = 0;
      
      const hasSoqQueride = 
        row.SOQOverride !== undefined &&
        row.SOQOverride !== null &&
        row.SOQOverride !== "";

    
        let adTypeOptions = secondDropdownOptions.map((i) => i.value);
        let scenarioPriceOptions = firstDropdownOptions.map((i) => i.value);
        const scenarioSelectionFlag = (adTypeOptions.includes(row.AdTypeScenario) && scenarioPriceOptions.includes(row.ScenarioUnitPrice) && !hasFinalForecast) ? 1 : 0;

        let adjustedForecast = parseFloat(row.FinalAdjustedForecast) || 0;
        let userForecast = 0;
        let userSelection = 0;
        let adjustSoQ = parseFloat(row.SOQOverride);
       
        // Use optional chaining and nullish coalescing operator to set default viewBy value to "Cases" if it's not defined
        const viewBy = selectedFilterData?.viewBy ?? "cases";

        if (hasFinalForecast) {
          userSelection = 1;
          
          userSoqSelection = 0;
          if (viewBy.includes("po")) {
            // View by Pounds
            // adjustedForecast = parseFloat(row.FinalForecastOverride);
            userForecast = parseFloat(row.FinalForecastOverride);
            adjustSoQ = parseFloat(row.SOQOverride)
          } else if (viewBy.includes("cases")) {
            // View by Cases
            // adjustedForecast = parseFloat(row.FinalForecastOverride) * parseFloat(row.NetUnitPounds);
            adjustedForecast = adjustedForecast * parseFloat(row.NetUnitPounds);
            userForecast = parseFloat(row.FinalForecastOverride) * parseFloat(row.NetUnitPounds);
            adjustSoQ = parseFloat(row.SOQOverride) * parseFloat(row.NetUnitPounds)
          } else {
            // Default case
            // adjustedForecast = parseFloat(row.FinalForecastOverride) * parseFloat(row.NetUnitPounds);
            adjustedForecast = adjustedForecast * parseFloat(row.NetUnitPounds);
            userForecast = parseFloat(row.FinalForecastOverride) * parseFloat(row.NetUnitPounds);;
            adjustSoQ = parseFloat(row.SOQOverride) * parseFloat(row.NetUnitPounds);
          }
        } else {
          // No final forecast provided
          adjustedForecast = (adjustedForecast * parseFloat(row.NetUnitPounds)) || 0;
          adjustSoQ = parseFloat(row.SOQOverride) * parseFloat(row.NetUnitPounds);

          userForecast = 0;
          userSelection = 0;
        }
        if(hasSoqQueride){          
          userSoqSelection = 1;
        }


        return {
          /* Final adjusted forecast */
          AdjustedForecast: adjustedForecast,
          /* if user entered value, then send 1 otherwise 0 */
          UserSelection: userSelection,
          /*  */
          ScenarioSelection: scenarioSelectionFlag/*  parseInt(row.ForecastNewStores || "0", 10) || 0 */,
          AdjustedSOQ: adjustSoQ,
          BranchId: String(row.BranchId || ""),
          ErpItemId: String(row.ErpItemId || ""),
          Initial_AdjustedSOQ: parseFloat(row.AdjustedSOQ || "0") * parseFloat(row.NetUnitPounds || "0"),
          PageNumber: String(row.PageNumber || ""),
          PriceMultiplier: (parseFloat(row.ScenarioUnitPrice || "0") || 0) / 100,
          ScenarioPrice: parseFloat(
            row.FinalUnitPrice.replace(/[^\d.-]/g, "") || row.ScenarioPrice
          ) || 0,
          UserForecast: userForecast,
          UserSOQSelection: userSoqSelection, //parseInt(row.UserSOQSelection || "0", 10) || 0,
          aFlag: parseInt(row.aFlag) || 0,
          forecast_date: initialTotalSummaryInput.forecast_date || "",
          isPoundSelected: row.isPoundSelected || false,
          pFlag: 0, // parseInt(row.pFlag) || 0,
          replicate_date: initialTotalSummaryInput.replicate_date || "",
        };
      }),
    };

    try {
      console.log("payload", payload);

      const response = await updateScenarioData(payload).unwrap();
      setModifiedRows([]);
      handleDataChange(tableData, []);
    } catch (err) {
      console.error("Error updating scenario data:", err);
    }
  };
  const handleUpdateSOQ = async () => {
    try {
      const response = await getUpdateSoq({}).unwrap();
      await fetchData(currentPage, tempFilters);

    } catch (err) {
      console.error("Error fetching SOQ data:", err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportScenario({}).unwrap();
      if (response.data) {
        prepareExport(response.data);
      }
    } catch (err) {
      console.error("Error exporting scenario data:", err);
    }
  };

  const formatNumber = (num: number): string => num.toLocaleString(); // Utility function

  const prepareExport = (data: any[]) => {
    // Prepares the data by mapping the `data` array to `ExportData` format
    const finalData = data.map((element: any) => ({
      BranchId: element.BranchId || "",
      BranchName: element.BranchName || "",
      ErpItemId: element.ErpItemId || "",
      forecast_date: element?.forecast_date?.slice(0, 10) || "",
      replicate_date: element?.replicate_date?.slice(0, 10) || "",
      AvgTotalPoundsAd_6Weeks: formatNumber(
        (element.AvgTotalPoundsAd_6Weeks || 0).toFixed(0)
      ),
      AvgTotalPoundsFlow_6Weeks: formatNumber(
        (element.AvgTotalPoundsFlow_6Weeks || 0).toFixed(0)
      ),
      ForecastWeek_0: formatNumber((element.ForecastWeek_0 || 0).toFixed(0)),
      ForecastWeek_1: formatNumber((element.ForecastWeek_1 || 0).toFixed(0)),
      ForecastWeek_2: formatNumber((element.ForecastWeek_2 || 0).toFixed(0)),
      ForecastWeek_3: formatNumber((element.ForecastWeek_3 || 0).toFixed(2)),
      ForecastWeek_4: formatNumber((element.ForecastWeek_4 || 0).toFixed(0)),
      ExpectedPrice: element.ExpectedPrice
        ? `$${element.ExpectedPrice.toFixed(2)}`
        : "$0.00",
      Adjustedforecast: formatNumber(
        (element.Adjustedforecast || 0).toFixed(0)
      ),
      SOQ_2Pounds: formatNumber((element.SOQ_2Pounds || 0).toFixed(0)),
      SOQPounds: formatNumber((element.SOQPounds || 0).toFixed(0)),
      VendorName: element.VendorName || "",
      itemDescription: element.itemDescription || "",
      AdjustedSOQ: formatNumber((element.AdjustedSOQ || 0).toFixed(0)),
      newForecast: formatNumber((element.newForecast || 0).toFixed(0)),
      Alerts: element.Alerts || "",
      Category: element.Category || "",
      SubCategory: element.SubCategory || "",
      UCode: element.UCode || "",
      NetUnitPounds: formatNumber((element.NetUnitPounds || 0).toFixed(0)),
      UpdatedBy: element.UpdatedBy || "",
      UpdatedAt: element?.UpdatedAt?.slice(0, 10) || "",
      expectedPLUAdType: element.expectedPLUAdType || "",
    }));

    // Dynamically generate headers based on the keys in `finalData`
    const headers = Object.keys(finalData[0] ?? {}).map((key) => ({
      label: key.replace(/_/g, " "), // Replace underscores in the key for better headers
      key, // Stay consistent with the key from `finalData`
    }));

    // Set the formatted data and headers in state
    setExportDataSet(finalData);
    setExportColumns(headers);
  };

  const formatFilterName = (filterKey: string) =>
    filterKey
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  useEffect(() => {
    fetchData(currentPage, tempFilters);
  }, [currentPage]);

  useEffect(() => {
    fetchFilterData(filters);
  }, []);

  return (
    <>
      <div className="relative">
        <div className={`flex flex-col gap-1 px-[2%] border-b-[1px] shadow ${!isVisible ? "pt-[8px] pb-[2px]" : "pt-[8px] pb-[15px]"}`}>
          <div className="flex justify-between items-center mb-1">
            <h3 className="page-title font-semibold">{"Collaborative Planning > Consensus Planning"}</h3>
            {<div className="flex gap-2">
              <img src={reload} alt="reload" className="w-[1vw]" />
              <p className="text-fs16 text-[#858585] font-semibold">Last Updated : <span>{moment(initialTotalSummaryInput.lastUpdated?.slice(0, 10)).format("MM/DD/YYYY")}</span></p>
            </div>}
          </div>

          {/* Filters Section */}
          {isVisible && (
            <div className="grid grid-cols-[1fr_auto] items-end gap-4">
              <div className="grid grid-cols-12 gap-2 items-end">
                {/* Render Dynamic Filters */}
                {filters &&
                  Object.entries(filters).map(
                    ([filterKey, filterArray]: [string, any[]]) => (
                      <FilterComponent
                        loading={isFilterLoading}
                        key={filterKey}
                        filterKey={filterKey}
                        filterName={
                          filterKey === "viewBy"
                            ? "View By"
                            : filterKey === "adjustmentWeek"
                              ? "Select Week for Adjustment"
                              : filterKey === "dc" || filterKey === "Dc"
                                ? "DC"
                                : filterKey === "vendorName"
                                  ? "Vendor"
                                  : filterKey === "dc" ? "DC" :
                                    filterKey === "vendorName" ? "Vendor" :
                                      formatFilterName(filterKey)
                        }
                        options={filterArray.map((opt) => ({
                          label: opt.filterValue,
                          value: opt.filterValue,
                        }))}
                        labelKey="label"
                        valueKey="value"
                        value={selectedFilterData[filterKey] || []}
                        onFilterChange={handleFilterChange}
                        onDropdownOpen={filterHandler}
                      />
                    )
                  )}

                {/* Render Static Filters */}
                {Object.entries(staticFilters).map(([filterKey, filterArray]: [string, any[]]) => {
                if (filterKey === "viewBy") {
                // Render "View By" filter as a radio button group
                return (
                  <div key={filterKey} className="flex flex-col w-full">
                    <p className="filter-header font-semibold">View By</p>
                    <SelectPicker
                    className="text-gray-500"
                      data={filterArray.map((opt) => ({
                        label: opt.displayValue,
                        value: opt.filterValue
                      }))}
                      size="xs"
                      defaultValue="cases" // Default selection for "Cases"
                      value={selectedFilterData[filterKey]?.[0] || "cases"}
                      onChange={(val: any) => handleFilterChange(filterKey, [val])}
                      disabled={isFilterLoading}
                      placeholder="Select an option"
                      block
                      searchable={false} // Optional: if you don't want it searchable
                      cleanable={false} // Optional: disallow clearing the selection
                    />
                  </div>
                )
        } else {
          // Use FilterComponent for other filters
          return (
            <FilterComponent
              loading={isFilterLoading}
              key={filterKey}
              filterKey={filterKey}
              filterName={
                filterKey === "adjustmentWeek"
                  ? "Select Week for Adjustment"
                  : filterKey.charAt(0).toUpperCase() + filterKey.slice(1)
              }
              options={filterArray.map((opt) => ({
                label: opt.displayValue,
                value: opt.filterValue
              }))}
              labelKey="label"
              valueKey="value"
              value={
                filterKey === "adjustmentWeek"
                  ? selectedFilterData[filterKey]?.length
                    ? selectedFilterData[filterKey]
                    : ["3"]
                  : selectedFilterData[filterKey] || []
              }
              onFilterChange={handleFilterChange}
              onDropdownOpen={filterHandler}
            />
          );
        }
      })}
              </div>
              <div className="align-right flex gap-1">
                <Button onClick={handleClearFilters} className="bg-[#949494] text-white px-[20px] h-[4vh] rounded font-semibold btn-text">
                  Clear
                </Button>

                <Button onClick={handleApplyFilters} className="bg-deloitteGreen text-white px-[20px] h-[4vh] rounded font-semibold btn-text">
                  Apply
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-center absolute left-2/4 top-full translate-x-[-50%] translate-y-[-50%] cursor-pointer z-[10]" onClick={filterShowHandler}>
            {isVisible && <img className="w-[1.5vw]" src={upArrow} alt="upArrow" />}
            {!isVisible && <img className="w-[1.5vw]" src={downArrow} alt="downArrow" />}
          </div>
        </div>
      </div>

      {isLoading && (
        <div>
          <CustomLoader open={isLoading} />
        </div>
      )}
      <div className="bg-[#F1F1F1] flex px-2 py-2"></div>
      {!isLoading && !error && (
        <TableComponent
          data={tableData}
          loading={isLoading}
          columns={columns}
          totalRows={totalRows}
          rowsPerPage={10}
          currentPage={currentPage}
          tableHeight={tableHeight}
          onPageChange={(page) => setCurrentPage(page)}
          onDataChange={handleDataChange}
          onRowSelect={handleRowSelect}
          onDropdownAction={handleRowDropdownAction}
          onUpdate={handleUpdateSOQ}
          onSave={handleSave}
          onExport={handleExport}
          exportData={exportDataSet}
          exportHeaders={exportColumns}
        />
      )}
    </>
  );
};

export default Consensus;
